import { z } from 'zod';

import { AI_UNAVAILABLE_REASONS } from '../../ai/ai-unavailable/ai-unavailable';

/**
 * Why a face-match check produced no similarity.
 *
 * The AI platform's own reasons, **plus** the three that are not its doing: the
 * case reached submission without both images (`evidence_missing`), the stored
 * object could not be read back (`evidence_unreadable`), and the ID was handed
 * in as a document rather than a photograph, so there is no face to embed
 * (`id_not_an_image`, F1).
 *
 * A **coded** set rather than a sentence, because the api has no error i18n
 * (standing issue) and this is read on a staff surface that must render in
 * Arabic. It is also why nothing here is ever written into the member-visible
 * step note: a member cannot act on "the budget cap was reached", and telling
 * them their faces did not match is a fraud tutorial.
 */
export const FACE_MATCH_UNAVAILABLE_REASONS = [
  ...AI_UNAVAILABLE_REASONS,
  'evidence_missing',
  'evidence_unreadable',
  'id_not_an_image',
] as const;

export const FaceMatchUnavailableReasonSchema = z.enum(FACE_MATCH_UNAVAILABLE_REASONS);

export type FaceMatchUnavailableReason = (typeof FACE_MATCH_UNAVAILABLE_REASONS)[number];

const REASON_SET: ReadonlySet<string> = new Set<string>(FACE_MATCH_UNAVAILABLE_REASONS);

/** Narrows a stored `face_match_checks.unavailable_reason`. */
export function isFaceMatchUnavailableReason(value: string): value is FaceMatchUnavailableReason {
  return REASON_SET.has(value);
}

/**
 * One advisory comparison of a case's ID photograph against its selfie
 * (`03-verification.md` §face_match).
 *
 * **Advisory means advisory.** Nothing in the platform reads this to decide a
 * case: `VERIFICATION_CASE_TRANSITIONS` has no edge from `submitted` to
 * `approved`/`rejected`, so only a human reviewer moves a case, and this shape
 * exists to inform that human. It is on the **staff** detail only —
 * `VerificationCaseSchema` has no property for it, because a member who can read
 * their own similarity score can tune a forgery against it.
 *
 * Every produced field is nullable together: when the check could not run,
 * `unavailableReason` says why and the rest is null. `thresholdAtTime` is the
 * exception and is **always** present — it is the snapshot of
 * `verification.faceMatchThreshold` as it stood when the check ran, so an
 * operator retuning the setting cannot silently re-judge decisions already
 * taken. `dimensions` records the width the two vectors were compared at, which
 * is what makes a score from before a model change readable afterwards.
 */
export const FaceMatchCheckSchema = z
  .object({
    /** Cosine similarity of the two embeddings — `[-1, 1]`, not `[0, 1]`. */
    similarity: z.number().min(-1).max(1).nullable(),
    thresholdAtTime: z.number().min(0).max(1),
    dimensions: z.number().int().positive().nullable(),
    provider: z.string().nullable(),
    model: z.string().nullable(),
    unavailableReason: FaceMatchUnavailableReasonSchema.nullable(),
    checkedAt: z.iso.datetime(),
  })
  .strict();

export type FaceMatchCheck = z.infer<typeof FaceMatchCheckSchema>;

/**
 * Whether the check agreed the two faces are the same person — or `null` when
 * it never ran.
 *
 * Derived rather than stored, so the verdict and the numbers it comes from can
 * never disagree (U3). `null` is a real third answer a caller must render: "we
 * do not know" is not "they disagree", and a badge that collapsed the two would
 * put a reviewer off a legitimate member because a budget cap was reached.
 */
export function faceMatchAgrees(check: FaceMatchCheck): boolean | null {
  return check.similarity === null ? null : check.similarity >= check.thresholdAtTime;
}
